import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, SITE } from "@/lib/siteConfig";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#E2E8F0]/70 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="font-display text-2xl text-[#1E2761] tracking-tight leading-none">
            NextChapter<span className="text-[#C9A84C]">4</span>You
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm tracking-wide transition-colors duration-200 relative ${
                  isActive
                    ? "text-[#1E2761] font-semibold"
                    : "text-[#334155] hover:text-[#1E2761]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.name}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-0 w-6 h-[2px] bg-[#C9A84C]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="ml-4 inline-flex items-center bg-[#1E2761] text-white px-5 py-2.5 text-sm font-medium tracking-wide hover:bg-[#161d4b] transition-colors duration-200"
          >
            Get in Touch
          </Link>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#1E2761] p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2E8F0]" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base border-b border-[#E2E8F0] last:border-none ${
                    isActive ? "text-[#1E2761] font-semibold" : "text-[#334155]"
                  }`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-contact"
              className="mt-4 inline-flex items-center justify-center bg-[#1E2761] text-white px-5 py-3 text-sm font-medium"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
